import { useState, useEffect } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useLanguage } from '../shared/LanguageContext'
import PortalHeader from '../shared/PortalHeader'
import { fetchAll, addGoal, fetchStudentIdentity } from '../shared/api'
import HomeworkSection from './HomeworkSection'
import { portalToday } from '../shared/portalToday'
import PortalSectionDegradedBanner from '../shared/PortalSectionDegradedBanner'
import { classLabelUsesTime, classPrimaryLabel, classSecondaryLine } from '../shared/classLabel'
import TruncatedText from '../shared/TruncatedText'
import { buildProgressTrend, formatDate, formatDateWithYear, formatMessageDate, summarizeAttendance, assignmentsBySession, assignmentStatus, assignmentStatusLabel, assignmentChipClass, feedbackWindow, feedbackKnownFor } from './ParentLayout.helpers'
import BillingSection from './BillingSection'

export default function ParentLayout() {
  const { studentIds, portalToken } = useAuth()
  const { t, language } = useLanguage()
  const [activeStudentId, setActiveStudentId] = useState(studentIds[0] || null)
  const [studentNames, setStudentNames] = useState({})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch student names for display
  useEffect(() => {
    if (!studentIds || studentIds.length === 0 || !portalToken) return
    let cancelled = false

    async function loadStudentNames() {
      const results = await Promise.all(
        studentIds.map(async (sid) => {
          const result = await fetchStudentIdentity(sid, portalToken)
          return [sid, result]
        })
      )

      if (cancelled) return

      const nameMap = {}
      for (const [sid, result] of results) {
        if (result?.data?.name) {
          nameMap[sid] = result.data.name
        }
      }
      setStudentNames(nameMap)
    }

    loadStudentNames()
    return () => { cancelled = true }
  }, [studentIds, portalToken])

  // Load ALL data in one shot
  useEffect(() => {
    if (!activeStudentId || !portalToken) return
    let cancelled = false
    setLoading(true)
    fetchAll(activeStudentId, portalToken).then(result => {
      if (cancelled) return
      setData(result)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [activeStudentId, portalToken])

  const currentStudentName = studentNames[activeStudentId] || ''

  // Reload goals after adding one
  function reloadData() {
    if (!activeStudentId || !portalToken) return
    fetchAll(activeStudentId, portalToken).then(result => {
      setData(result)
    })
  }

  return (
    <div className="portal-shell">
      <PortalHeader studentName={currentStudentName} freshness={data?.sync} />

      {studentIds.length > 1 && (
        <div className="child-switcher">
          {studentIds.map(sid => (
            <button
              key={sid}
              className={`child-switcher-btn ${activeStudentId === sid ? 'active' : ''}`}
              onClick={() => setActiveStudentId(sid)}
            >
              {studentNames[sid] || `Student ${sid}`}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <div className="portal-content portal-content--single">
          <ScheduleSection schedule={data?.schedule} progress={data?.progress} homework={data?.homework} t={t} language={language} />
          <HomeworkSection homework={data?.homework} progress={data?.progress} t={t} />
          <ProgressSection progress={data?.progress} schedule={data?.schedule} t={t} />
          <GoalsSection
            goals={data?.goals}
            studentId={activeStudentId}
            portalToken={portalToken}
            t={t}
            onGoalAdded={reloadData}
          />
          <BillingSection billing={data?.billing} t={t} />
          <MessagesSection messages={data?.messages} language={language} t={t} />
        </div>
      )}
    </div>
  )
}


/* ─── Section Components ──────────────────────────────────────── */

// ebc91c93: session learning objectives + "what we did" render as a header
// ABOVE per-student feedback (render-time; session_objectives and
// session_notes.session_summary stay the single sources). Renders nothing
// when both are absent — no empty chrome.
function SessionHeaderBlock({ objectives, sessionSummary, t }) {
  const hasObjectives = Array.isArray(objectives) && objectives.length > 0
  if (!hasObjectives && !sessionSummary) return null
  return (
    <div className="session-header-block" style={{ margin: '2px 0 6px', fontSize: '12px' }}>
      {hasObjectives && (
        <div style={{ marginBottom: '2px' }}>
          <span style={{ color: 'var(--text-tertiary)' }}>{t('learningObjective')}{t('labelColon')}</span>
          <span style={{ color: 'var(--text-secondary)' }}>{objectives.join('; ')}</span>
        </div>
      )}
      {sessionSummary && (
        <div>
          <span style={{ color: 'var(--text-tertiary)' }}>{t('whatWeDid')}{t('labelColon')}</span>
          <span style={{ color: 'var(--text-secondary)' }}>{sessionSummary}</span>
        </div>
      )}
    </div>
  )
}

function ScheduleSection({ schedule, progress, homework, t, language }) {
  const loadError = schedule?.error
  const [expanded, setExpanded] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)
  const schedData = schedule?.data || schedule
  const upcoming = schedData?.upcoming || []
  const classes = schedData?.classes || []
  const past = schedData?.past || []
  const nextSession = upcoming[0] || null
  const restUpcoming = upcoming.slice(1)

  // Build lookup for feedback + homework by session
  const progressLoadError = progress?.error
  const progData = progress?.data || progress
  const feedbackEntries = progData?.entries || []
  const assignments = homework?.data || homework || []
  // #596: assignments keyed to the session they were assigned in, not to
  // every session of the class.
  const assignmentsForSession = assignmentsBySession(assignments, past)
  // NOTE: `past` is the capped, `< today` list — an assignment created for a
  // session closed out today appears in the schedule tomorrow (Homework
  // section lists it immediately).
  const scheduleToday = portalToday()
  // #597: the homework chips share the schedule's own knowledge of what
  // feedback was fetched (failed read / 50-row cap → unknown, never "none").
  const homeworkFeedbackWindow = feedbackWindow(progress)
  // #597: what feedback the portal actually holds is decided once, in the
  // shared helper (failed read / 50-row cap → unknown, never "none").

  // Class id → class row. The parent-facing alias lives on the class, the
  // effective time on the session, so a label needs both.
  const classById = Object.fromEntries(classes.map(cls => [cls.id, cls]))

  // Every schedule label routes through the shared helper (contract rule 1-2).
  // The internal class `name` is deliberately absent from this chain.
  function sessionLabel(session) {
    const cls = classById[session.classId] || {}
    const displayName = cls.displayName
    const timeDisplay = session.timeDisplay
    return {
      primary: classPrimaryLabel({ displayName, timeDisplay, t }),
      secondary: classSecondaryLine({
        sessionDate: session.sessionDate,
        timeDisplay,
        sessionNumber: session.sessionNumber,
        language,
        usedTimeAsPrimary: classLabelUsesTime({ displayName, timeDisplay }),
      }),
    }
  }

  const nextLabel = nextSession ? sessionLabel(nextSession) : null

  return (
    <div className="portal-section">
      <div className="portal-section-card portal-card-blue">
        <div className="portal-section-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="2" x2="9" y2="6"/><line x1="15" y1="2" x2="15" y2="6"/></svg>
          {t('schedule')}
        </div>
        <PortalSectionDegradedBanner error={loadError} t={t} />
        {loadError ? null : !nextSession ? (
          <div className="portal-section-empty">{t('noUpcomingSessions')}</div>
        ) : (
          <div className="next-class-highlight">
            <div className="portal-section-sublabel">{t('nextClass')}</div>
            <div className="session-item">
              <div className="session-info">
                <span className="session-date-text">{nextLabel.primary}</span>
                {nextLabel.secondary && (
                  <span className="session-number-text">{nextLabel.secondary}</span>
                )}
              </div>
            </div>
          </div>
        )}

        <button
          className="collapsible-toggle"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '−' : '+'} {expanded ? t('hideFullSchedule') : t('showFullSchedule')}
        </button>

        {expanded && (
          <>
            {restUpcoming.length > 0 && (
              <div className="upcoming-sessions-list">
                <p className="portal-section-sublabel">{t('upcomingSessions')}</p>
                {restUpcoming.map(s => {
                  const label = sessionLabel(s)
                  return (
                    <div key={s.id} className="session-item">
                      <div className="session-info">
                        <span className="session-date-text">{label.primary}</span>
                        {label.secondary && (
                          <span className="session-number-text">{label.secondary}</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {classes.length > 0 && (
              <div className="schedule-classes-list">
                <p className="portal-section-sublabel">{t('classes')}</p>
                {classes.map(cls => {
                  // Classes-list rows carry no session date, so there is no
                  // weekday to derive: primary per rule 1, secondary is the
                  // raw time string, and nothing at all when the primary
                  // already IS the time (contract rule 3).
                  const primary = classPrimaryLabel({
                    displayName: cls.displayName,
                    timeDisplay: cls.timeDisplay,
                    t,
                  })
                  const showTime = !classLabelUsesTime({
                    displayName: cls.displayName,
                    timeDisplay: cls.timeDisplay,
                  }) && cls.timeDisplay
                  return (
                    <div key={cls.id} className="schedule-class-row">
                      <span className="class-name">{primary}</span>
                      {cls.role === 'ta' ? (
                        // #589: the student's own TA shift stays visible, labelled.
                        <span className="class-role-badge" data-role="ta">{t('taRole')}</span>
                      ) : null}
                      {showTime ? <span className="class-time">{cls.timeDisplay}</span> : null}
                    </div>
                  )
                })}
              </div>
            )}

            {past.length > 0 && (
              <div className="past-sessions-list">
                <p className="portal-section-sublabel">{t('pastSessions')}</p>
                {past.map(s => {
                  const isSelected = selectedSession === s.id
                  const label = sessionLabel(s)
                  // Stable class id + session number is the canonical join.
                  // Internal names can repeat across semesters, so name is a
                  // deploy-skew fallback ONLY when an older payload omitted
                  // classId; a present-but-mismatched id must fail closed.
                  const sessionFeedback = feedbackEntries.find(f =>
                    f.sessionNumber === s.sessionNumber && (
                      f.classId != null
                        ? f.classId === s.classId
                        : f.className === s.className
                    )
                  )
                  const feedbackIsOutsideLoadedWindow = Boolean(
                    !sessionFeedback && !feedbackKnownFor(homeworkFeedbackWindow, s.sessionDate)
                  )
                  // Homework assigned IN this session (#596)
                  const classHomework = assignmentsForSession.get(s.id) || []
                  // #589 (operator decision 2026-09-07): a TA-shift row is date +
                  // TA label only — never expandable. The class-level recap and
                  // homework behind an expanded row belong to the class she
                  // assisted, not to her; the TA portal owns that content.
                  const isTaShift = classById[s.classId]?.role === 'ta'
                  if (isTaShift) {
                    return (
                      <div key={s.id} className="session-item session-item-ta-shift" data-ta-shift="true">
                        <div className="session-info">
                          <span className="session-date-text">{label.primary}</span>
                          {label.secondary && (
                            <span className="session-number-text">{label.secondary}</span>
                          )}
                          <span className="class-role-badge" data-role="ta">{t('taRole')}</span>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div key={s.id}>
                      <div
                        className={`session-item session-item-clickable${isSelected ? ' session-item-selected' : ''}`}
                        onClick={() => setSelectedSession(isSelected ? null : s.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="session-info">
                          <span className="session-date-text">{label.primary}</span>
                          {label.secondary && (
                            <span className="session-number-text">{label.secondary}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {s.attendanceStatus && (
                            <span className={`attendance-badge attendance-${s.attendanceStatus}`}>
                              {t(s.attendanceStatus)}
                            </span>
                          )}
                          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{isSelected ? '▾' : '▸'}</span>
                        </div>
                      </div>

                      {isSelected && (
                        <div className="session-detail" onClick={e => e.stopPropagation()}
                             style={{ padding: '10px 12px', margin: '0 0 8px', background: 'var(--bg)', borderRadius: 'var(--radius)', fontSize: '12px' }}>
                          {progressLoadError ? (
                            <PortalSectionDegradedBanner error={progressLoadError} t={t} />
                          ) : sessionFeedback ? (
                            <>
                              <SessionHeaderBlock
                                objectives={sessionFeedback.objectives}
                                sessionSummary={sessionFeedback.sessionSummary}
                                t={t}
                              />
                              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                {['participation', 'motivation', 'homework', 'metObjective'].map(key => {
                                  const val = sessionFeedback[key]
                                  // 2026-09: the teacher's explicit "not submitted" verdict
                                  // renders beside the scores in red; null stays hidden.
                                  if (key === 'homework' && val == null && sessionFeedback.homeworkSubmitted === false) {
                                    return (
                                      <span key={key} style={{ color: 'var(--text-secondary)' }}>
                                        {t('homework')}: <strong style={{ color: 'var(--accent-red)' }}>{t('notSubmitted')}</strong>
                                      </span>
                                    )
                                  }
                                  if (val == null) return null
                                  const scoreLabel = t(key === 'metObjective' ? 'metObjective' : key)
                                  return (
                                    <span key={key} style={{ color: 'var(--text-secondary)' }}>
                                      {scoreLabel}: <strong style={{ color: val >= 4 ? 'var(--accent-green)' : val >= 3 ? 'var(--accent-orange)' : 'var(--accent-red)' }}>{val}/5</strong>
                                    </span>
                                  )
                                })}
                              </div>
                              {sessionFeedback.feedbackText && (
                                <TruncatedText
                                  text={sessionFeedback.feedbackText}
                                  t={t}
                                  quoted
                                  style={{ margin: '4px 0 0', color: 'var(--text-primary)', fontStyle: 'italic' }}
                                />
                              )}
                            </>
                          ) : (
                            <span style={{ color: 'var(--text-tertiary)' }}>
                              {t(feedbackIsOutsideLoadedWindow ? 'olderFeedbackNotLoaded' : 'noFeedback')}
                            </span>
                          )}

                          {classHomework.length > 0 && (
                            <div style={{ marginTop: '8px', borderTop: '1px solid var(--border)', paddingTop: '6px' }}>
                              <span style={{ fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>📝 {t('homework')}</span>
                              {classHomework.map(a => {
                                const derived = assignmentStatus(a, scheduleToday, homeworkFeedbackWindow)
                                const statusKey = derived.key
                                const chipClass = assignmentChipClass(statusKey, derived.score)
                                return (
                                  <div key={a.id} style={{ marginTop: '4px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                    <span>
                                      {a.title}
                                      {a.due_date && (
                                        <span style={{ marginLeft: '6px', color: 'var(--text-tertiary)' }}>{t('dueDate')}: {formatDate(a.due_date)}</span>
                                      )}
                                    </span>
                                    <span className={`submission-status ${chipClass}`}>{assignmentStatusLabel(derived, t)}</span>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ProgressSection({ progress, schedule, t }) {
  const loadError = progress?.error
  const progData = progress?.data || progress
  const schedData = schedule?.data || schedule
  const averages = progData?.averages
  const entries = progData?.entries || []
  const [showEarlierFeedback, setShowEarlierFeedback] = useState(false)
  // Historical feedback also lives behind the schedule's past-session rows;
  // this toggle makes it discoverable HERE, where parents look for it. One
  // constant feeds both the slice and the toggle count so they cannot drift.
  const RECENT_FEEDBACK_COUNT = 3
  const visibleEntries = showEarlierFeedback
    ? entries
    : entries.slice(0, RECENT_FEEDBACK_COUNT)
  const earlierFeedbackCount = Math.max(0, entries.length - RECENT_FEEDBACK_COUNT)
  const latestEntry = entries[0] || null
  const attendanceSummary = summarizeAttendance(schedData)
  const trend = buildProgressTrend(entries)
  // The teacher-update block is the surface that historically leaked internal
  // class codes to parents. It now runs the same fallback chain as the
  // schedule rows; the time half of that chain lives on the schedule classes
  // map, joined by class id (aliases and names are both ambiguous keys).
  const scheduleClassById = Object.fromEntries(
    (schedData?.classes || []).map(cls => [cls.id, cls])
  )

  const scores = averages ? [
    { key: 'participation', label: t('participation'), value: averages.participation },
    { key: 'motivation', label: t('motivation'), value: averages.motivation },
    { key: 'homework', label: t('homework'), value: averages.homework },
    { key: 'met_objective', label: t('metObjective'), value: averages.met_objective },
  ].filter(({ value }) => Number.isFinite(value)) : []

  return (
    <div className="portal-section">
      <div className="portal-section-card portal-card-green">
        <div className="portal-section-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          {t('progress')}
        </div>
        <PortalSectionDegradedBanner error={loadError} t={t} />
        {!loadError && (!averages || averages.count === 0 ? (
          <div className="portal-section-empty">{t('noFeedback')}</div>
        ) : (
          <>
            <div className="progress-summary-grid">
              <div className="progress-summary-card">
                <span className="progress-summary-label">{t('attendanceRate')}</span>
                <span className="progress-summary-value">
                  {attendanceSummary.label}
                </span>
              </div>
              <div className="progress-summary-card">
                <span className="progress-summary-label">{t('feedbackSessions')}</span>
                <span className="progress-summary-value">{averages.count}</span>
              </div>
              <div className="progress-summary-card">
                <span className="progress-summary-label">{t('latestFeedbackDate')}</span>
                <span className="progress-summary-value">
                  {latestEntry?.sessionDate ? formatDate(latestEntry.sessionDate) : '—'}
                </span>
              </div>
            </div>

            {trend && (
              <div className="progress-trend-panel">
                <div className="portal-section-sublabel">{t('progressTrend')}</div>
                <svg className="progress-trend-chart" viewBox="0 0 240 72" preserveAspectRatio="none" aria-label={t('progressTrend')}>
                  <path className="progress-trend-area" d={`${trend.areaPath} L 232 60 L 8 60 Z`} />
                  <path className="progress-trend-line" d={trend.linePath} />
                  {trend.points.map((point) => (
                    <circle
                      key={point.label}
                      className="progress-trend-point"
                      cx={point.x}
                      cy={point.y}
                      r="3.5"
                    />
                  ))}
                </svg>
                <div className="progress-trend-labels" aria-hidden="true">
                  <span>{trend.firstLabel}</span>
                  <span>{trend.lastLabel}</span>
                </div>
              </div>
            )}

            {scores.length > 0 && (
              <div className="score-bars">
                {scores.map(s => {
                  const pct = Math.round((s.value / 5) * 100)
                  const tier = s.value >= 4 ? 5 : s.value >= 3 ? 3 : 1
                  const colorClass = `score-color-${tier}`
                  return (
                    <div key={s.key} className="score-bar-row">
                      <span className="score-bar-label">{s.label}</span>
                      <span className={`score-bar-number ${colorClass}`}>{s.value.toFixed(1)}</span>
                      <div className="score-bar-track">
                        <div
                          className={`score-bar-fill score-bar-${tier}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {visibleEntries.length > 0 && (
              <div className="progress-recent">
                <p className="portal-section-sublabel">{t('teacherUpdate')}</p>
                {visibleEntries.map(e => {
                  const classLabel = classPrimaryLabel({
                    displayName: e.classDisplayName,
                    timeDisplay: scheduleClassById[e.classId]?.timeDisplay,
                    t,
                  })
                  return (
                    <div key={e.id} className="feedback-timeline-item">
                      <span className="timeline-dot"></span>
                      <div className="feedback-content">
                        <span className="feedback-date">{formatDateWithYear(e.sessionDate)}: {classLabel}</span>
                        <SessionHeaderBlock
                          objectives={e.objectives}
                          sessionSummary={e.sessionSummary}
                          t={t}
                        />
                        {e.feedbackText && (
                          <TruncatedText className="feedback-text" text={e.feedbackText} t={t} quoted />
                        )}
                      </div>
                    </div>
                  )
                })}
                {earlierFeedbackCount > 0 && (
                  <button
                    type="button"
                    className="collapsible-toggle feedback-earlier-toggle"
                    aria-expanded={showEarlierFeedback}
                    onClick={() => setShowEarlierFeedback(prev => !prev)}
                  >
                    {showEarlierFeedback
                      ? t('hideEarlierFeedback')
                      : `${t('showEarlierFeedback')} (${earlierFeedbackCount})`}
                  </button>
                )}
              </div>
            )}
          </>
        ))}
      </div>
    </div>
  )
}

function GoalsSection({ goals, studentId, portalToken, t, onGoalAdded, setBy = 'parent' }) {
  const [showForm, setShowForm] = useState(false)
  const loadError = goals?.error
  const goalsData = goals?.data || goals || []
  const activeGoals = goalsData.filter(g => g.status === 'active')

  return (
    <div className="portal-section">
      <div className="portal-section-card portal-card-purple">
        <div className="portal-section-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
          {t('goals')}
        </div>
        <PortalSectionDegradedBanner error={loadError} t={t} />
        {activeGoals.length > 0 && (
          activeGoals.map(goal => (
            <div key={goal.id} className="goal-item">
              <div className="goal-header" style={{ gap: '6px' }}>
                <span style={{ fontSize: '16px', lineHeight: 1 }}>&bull;</span>
                <p className="goal-text" style={{ marginBottom: 0 }}>{goal.goalText}</p>
              </div>
              <div style={{ marginLeft: '22px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <span className={`goal-type-badge goal-type-${goal.goalType}`} style={{ opacity: 0.7, fontSize: '10px', padding: '1px 6px' }}>
                  {t(goal.goalType) || goal.goalType}
                </span>
                <span className={`goal-status-badge goal-status-${goal.status}`} style={{ opacity: 0.7, fontSize: '10px', padding: '1px 6px' }}>
                  {t(goal.status)}
                </span>
              </div>
              {goal.progressNotes && (
                <p className="goal-progress" style={{ marginLeft: '22px' }}>{goal.progressNotes}</p>
              )}
            </div>
          ))
        )}

        <button
          className="collapsible-toggle"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '−' : '+'} {setBy === 'student' ? t('setPersonalGoals') : t('suggestGoal')}
        </button>

        {showForm && (
          <GoalForm
            studentId={studentId}
            portalToken={portalToken}
            t={t}
            onAdded={() => { setShowForm(false); onGoalAdded() }}
            setBy={setBy}
          />
        )}
      </div>
    </div>
  )
}

function GoalForm({ studentId, portalToken, t, onAdded, setBy = 'parent' }) {
  const [goalText, setGoalText] = useState('')
  const [goalType, setGoalType] = useState('speaking')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!goalText.trim()) return

    setSubmitting(true)
    setFormError(null)

    const { error } = await addGoal(studentId, goalText.trim(), goalType, setBy, portalToken)

    if (error) {
      setFormError(error.message || error)
    } else {
      setGoalText('')
      setGoalType('speaking')
      if (onAdded) onAdded()
    }
    setSubmitting(false)
  }

  return (
    <form className="goal-form" onSubmit={handleSubmit}>
      {formError && (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          <span>{formError}</span>
        </div>
      )}
      <div className="goal-form-group">
        <label className="goal-form-label">{t('goals')}</label>
        <textarea
          className="goal-form-textarea"
          value={goalText}
          onChange={e => setGoalText(e.target.value)}
          placeholder={setBy === 'student' ? t('studentGoalPlaceholder') : t('goalPlaceholder')}
          required
        />
      </div>
      <div className="goal-form-group">
        <label className="goal-form-label">{t('goalType')}</label>
        <select
          className="goal-form-select"
          value={goalType}
          onChange={e => setGoalType(e.target.value)}
        >
          <option value="speaking">{t('speaking')}</option>
          <option value="thinking">{t('thinking')}</option>
          <option value="confidence">{t('confidence')}</option>
          <option value="fluency">{t('fluency')}</option>
          <option value="homework">{t('homework')}</option>
          <option value="tournament">{t('tournament')}</option>
          <option value="organization">{t('organization')}</option>
          <option value="independence">{t('independence')}</option>
          <option value="teamwork">{t('teamwork')}</option>
          <option value="other">{t('other')}</option>
        </select>
      </div>
      <button
        type="submit"
        className="goal-form-submit"
        disabled={submitting || !goalText.trim()}
      >
        {submitting ? t('submitting') : t('submit')}
      </button>
    </form>
  )
}

function MessagesSection({ messages, language, t }) {
  const loadError = messages?.error
  const [expanded, setExpanded] = useState(false)
  const items = messages?.data || messages || []
  const visibleItems = expanded ? items : items.slice(0, 3)

  return (
    <div className="portal-section">
      <div className="portal-section-card portal-card-slate">
        <div className="portal-section-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16v12H4z"/><path d="m4 7 8 6 8-6"/></svg>
          {t('messages')}
        </div>
        <PortalSectionDegradedBanner error={loadError} t={t} />
        {loadError ? null : items.length === 0 ? (
          <div className="portal-section-empty">{t('noMessages')}</div>
        ) : (
          <>
            <div className="portal-section-sublabel">{t('recentMessages')}</div>
            <div className="message-history-list">
              {visibleItems.map((message) => (
                <div key={message.id} className="message-item">
                  <div className="message-date">
                    {t('sentOn')}: {formatMessageDate(message.sentAt, language)}
                  </div>
                  <p className="message-body">
                    {language === 'cn' && message.messageCn ? message.messageCn : message.messageEn || message.messageCn}
                  </p>
                </div>
              ))}
            </div>
            {items.length > 3 && (
              <button
                className="collapsible-toggle"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? '−' : '+'} {expanded ? t('hideHistory') : t('showHistory')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}




export { BillingSection, ScheduleSection, HomeworkSection, ProgressSection, GoalsSection, MessagesSection, PortalSectionDegradedBanner, formatDate }
